import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { assertArrayPayload, assertObjectPayload } from "@utils/apiPayload";
import { isFetchingHomePromotionsAtom } from "@/store/promotionAtoms";
import type {
    CreatePromotionResponse,
    GetHomePromotionsResponse,
    HomePromotion,
    Promotion,
    PromotionRequest,
    UpdatePromotionRequest,
} from "@models/promotion";

const PROMOTIONS_ENDPOINT = "/promotions";

const TILE_COUNT = 3;

const isStringOrNull = (value: unknown): value is string | null =>
    value === null || typeof value === "string";

// One slot, checked on its own: every field rendering depends on must have
// its contract type, or the slot is empty. A promotion that cannot render
// correctly is dropped rather than taking the other banners down with it.
const toSlot = (value: unknown): HomePromotion | null => {
    if (typeof value !== "object" || value === null) return null;
    const {
        id,
        slot,
        label,
        ctaLabel,
        ctaHref,
        imageUrl,
        vendorId,
        vendorSlug: sentSlug,
    } = value as Record<string, unknown>;
    // #425. Absent reads as none: an API from before the vendor page sends no
    // slug, and that banner still links, to the vendor's grid.
    const vendorSlug = sentSlug === undefined ? null : sentSlug;
    if (
        typeof id !== "string" ||
        id === "" ||
        typeof slot !== "number" ||
        typeof label !== "string" ||
        !isStringOrNull(ctaLabel) ||
        !isStringOrNull(ctaHref) ||
        !isStringOrNull(imageUrl) ||
        !(vendorId === null || typeof vendorId === "number") ||
        !isStringOrNull(vendorSlug)
    ) {
        return null;
    }
    return {
        id,
        slot,
        label,
        ctaLabel,
        ctaHref,
        imageUrl,
        vendorId,
        vendorSlug,
    };
};

// Always three positions, Tile1→Tile3: a row that is not a list is three
// empty tiles; a list of the wrong length keeps its first three positions,
// padded with empty ones. A tile repeating an earlier tile's id is dropped —
// the row is keyed by id.
const toTilesRow = (value: unknown): (HomePromotion | null)[] => {
    const raw: unknown[] = Array.isArray(value) ? value : [];
    const seen = new Set<string>();
    return Array.from({ length: TILE_COUNT }, (_, i) => {
        const tile = toSlot(raw[i]);
        if (!tile || seen.has(tile.id)) return null;
        seen.add(tile.id);
        return tile;
    });
};

// callApi hands back a 2xx UNCHECKED (see utilities/apiPayload.ts). Only an
// envelope that is not an object fails the read; inside it each slot is
// checked on its own, so one bad slot hides one banner, not all of them.
const toHomePromotions = (
    endpoint: string,
    payload: unknown,
): GetHomePromotionsResponse => {
    assertObjectPayload(endpoint, payload);
    return {
        topHero: toSlot(payload.topHero),
        midPagePromo: toSlot(payload.midPagePromo),
        tilesRow: toTilesRow(payload.tilesRow),
    };
};

// The create and update multipart body (`Body.*` parts, as useProductApi
// sends them). A null field is left out: the update reads an absent optional
// as "clear it", and an absent image as "keep the current one".
const toPromotionForm = (
    request: PromotionRequest,
    isActive?: boolean,
): FormData => {
    const form = new FormData();
    form.append("Body.Slot", String(request.slot));
    form.append("Body.Label", request.label);
    if (request.ctaLabel !== null) form.append("Body.CtaLabel", request.ctaLabel);
    if (request.ctaHref !== null) form.append("Body.CtaHref", request.ctaHref);
    if (request.startsAt !== null) form.append("Body.StartsAt", request.startsAt);
    if (request.endsAt !== null) form.append("Body.EndsAt", request.endsAt);
    if (request.vendorId !== null) {
        form.append("Body.VendorId", String(request.vendorId));
    }
    if (isActive !== undefined) form.append("Body.IsActive", String(isActive));
    if (request.image !== null) form.append("Body.Image", request.image);
    return form;
};

export const usePromotionApi = () => {
    const { callApi } = useApi();
    const setIsFetching = useSetAtom(isFetchingHomePromotionsAtom);

    // GET /promotions/home (E14-S5). Not ship-to scoped — a banner is not a
    // product. The answer itself is held by the browse home, not an atom.
    // `skipErrorNotification` is opt-in, as on useProductApi.getPopular, for
    // a caller that handles the failure itself.
    const getHome = useCallback(
        async (
            options: { skipErrorNotification?: boolean } = {},
        ): Promise<GetHomePromotionsResponse> => {
            const endpoint = `${PROMOTIONS_ENDPOINT}/home`;
            setIsFetching(true);
            try {
                const response = await callApi<unknown>(endpoint, {
                    skipErrorNotification: options.skipErrorNotification,
                });
                return toHomePromotions(endpoint, response);
            } finally {
                setIsFetching(false);
            }
        },
        [callApi, setIsFetching],
    );

    // GET /promotions (E14-S4): the admin list, deactivated rows included.
    // A flat array with no paging envelope. The answer is held by the admin
    // page itself (no other reader), which decides from its own read.
    const getPromotions = useCallback(
        async (
            options: { skipErrorNotification?: boolean } = {},
        ): Promise<Promotion[]> => {
            const data = await callApi<unknown>(PROMOTIONS_ENDPOINT, {
                skipErrorNotification: options.skipErrorNotification,
            });
            // Checked, not assumed: a payload that is not a list must fail the
            // read, never render as "no promotions".
            assertArrayPayload(PROMOTIONS_ENDPOINT, data);
            return data as Promotion[];
        },
        [callApi],
    );

    const createPromotion = useCallback(
        (request: PromotionRequest): Promise<CreatePromotionResponse> =>
            callApi<CreatePromotionResponse>(PROMOTIONS_ENDPOINT, {
                method: "POST",
                body: toPromotionForm(request),
                contentType: "multipart",
            }),
        [callApi],
    );

    // A full replacement of the editable fields: an omitted optional is
    // cleared (except the image, which is kept when none is sent), and
    // IsActive is always stated.
    const updatePromotion = useCallback(
        async (id: string, request: UpdatePromotionRequest): Promise<void> => {
            await callApi(`${PROMOTIONS_ENDPOINT}/${id}`, {
                method: "PUT",
                body: toPromotionForm(request, request.isActive),
                contentType: "multipart",
            });
        },
        [callApi],
    );

    // A soft delete: the row stays, inactive, and Edit can reactivate it.
    const deletePromotion = useCallback(
        async (id: string): Promise<void> => {
            await callApi(`${PROMOTIONS_ENDPOINT}/${id}`, {
                method: "DELETE",
                contentType: "none",
            });
        },
        [callApi],
    );

    return {
        getHome,
        getPromotions,
        createPromotion,
        updatePromotion,
        deletePromotion,
    };
};
